import { BoxProps, chakra } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { toMultiPolygon2 } from '../domain/geometry/algorithms/util/type-mapping';
import { Point2 } from '../domain/geometry/geometric-types';
import { SheetShapeDeepFragment } from '../gql/graphql';
import { hasDbIds } from '../services/viewer-services';
import { DbIdsPerModel } from './common/ForgeViewer';
import { SheetViewerContextType, useSheetViewer } from './common/SheetViewer';
import SheetShapeLabel from './orderContractor/pdf-shapes/SheetShapeLabel';
import {
  getPolygon2PathData,
  transformMultipolygonCoordinates,
} from './orderContractor/shapes/util';

export const shapeUrnDataAttribute = 'data-shape-urn';
export const shapeDbIdDataAttribute = 'data-shape-db-id';

export const SheetShapes = ({
  getPointInViewerCoordinateSystem,
  showLabels,
}: {
  getPointInViewerCoordinateSystem: (pointInPdf: Point2) => Point2;
  showLabels: boolean;
}) => {
  const {
    shapesManager: { renderedSheetShapes },
    colorization: { colorization },
  } = useSheetViewer();

  const polygonMidPoint = (
    multipolygon: NonNullable<SheetShapeDeepFragment['sheetShapePolygon']>
  ) => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    multipolygon.multipolygon.polygons.forEach((polygon) => {
      polygon.exterior.points.forEach(({ x, y }) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
    });

    return getPointInViewerCoordinateSystem([
      (minX + maxX) / 2,
      (minY + maxY) / 2,
    ]);
  };
  return (
    <>
      <chakra.svg
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        pointerEvents="none"
        sx={{
          '> *': {
            // Make shapes interactive without preventing pointer events on the pdf page
            pointerEvents: 'auto',
          },
        }}
      >
        {Object.values(renderedSheetShapes)
          .flatMap((v) => v)
          .map((shape) => (
            <SheetShape
              key={shape.id}
              shape={shape}
              getPointInViewerCoordinateSystem={
                getPointInViewerCoordinateSystem
              }
            />
          ))}
      </chakra.svg>
      {showLabels
        ? Object.values(renderedSheetShapes)
            .flatMap((v) => v)
            .map((shape) => {
              if (shape.sheetShapePolygon) {
                return (
                  <SheetShapeLabel
                    key={shape.id}
                    label={shape.name}
                    position={polygonMidPoint(shape.sheetShapePolygon)}
                    color={getColorForShape(
                      shape.urn,
                      shape.dbId,
                      colorization,
                      false
                    )}
                  />
                );
              } else if (shape.sheetShapePoint) {
                return (
                  <SheetShapeLabel
                    key={shape.id}
                    label={shape.name}
                    position={getPointInViewerCoordinateSystem([
                      shape.sheetShapePoint.point.x,
                      shape.sheetShapePoint.point.y,
                    ])}
                    color={getColorForShape(
                      shape.urn,
                      shape.dbId,
                      colorization,
                      false
                    )}
                  />
                );
              }
            })
        : undefined}
    </>
  );
};

function getColorForShape(
  shapeUrn: string,
  shapeDbId: number,
  colorization: SheetViewerContextType['colorization']['colorization'],
  isSelected: boolean
): string {
  if (isSelected) {
    return 'blue.300';
  }
  if (
    !colorization ||
    !colorization.dbIdsPerModel[shapeUrn] ||
    !colorization.dbIdsPerModel[shapeUrn].includes(shapeDbId)
  ) {
    return 'gray.500';
  }

  return colorization.colorFunc(shapeUrn, shapeDbId);
}
const SheetShape = ({
  shape,
  getPointInViewerCoordinateSystem,
}: {
  shape: SheetShapeDeepFragment;
  getPointInViewerCoordinateSystem: (pointInPdf: Point2) => Point2;
}) => {
  const {
    selectedDbIds,
    toggleSelectDbId,
    selectDbIds,
    visibilityManager: { hiddenElements, isolatedElements, hiddenSheetShapes },
    colorization: { colorization },
    ghosting: { isGhostModeActive: isGhostingActive },
  } = useSheetViewer();

  const shapeUrn = shape.urn;
  const isSelected = selectedDbIds[shapeUrn]?.includes(shape.dbId);
  const isHidden = isShapeHidden(
    hiddenElements,
    shapeUrn,
    shape,
    hiddenSheetShapes,
    isolatedElements,
    isSelected
  );
  const color = getColorForShape(
    shapeUrn,
    shape.dbId,
    colorization,
    isSelected
  );
  if (isHidden && !isGhostingActive) {
    return null;
  }
  const ghostingStyle: BoxProps = getShapeGhostingStyle(
    isHidden,
    isGhostingActive
  );

  const onClick: React.MouseEventHandler = (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      toggleSelectDbId(shape.dbId, shapeUrn);
    } else {
      selectDbIds({ [shapeUrn]: [shape.dbId] });
    }
  };
  const dataAttributes = {
    [shapeUrnDataAttribute]: shapeUrn,
    [shapeDbIdDataAttribute]: shape.dbId,
  };
  if (shape.sheetShapePolygon) {
    return (
      <SheetPolygonShape
        sheetShapePolygon={shape.sheetShapePolygon}
        onClick={onClick}
        isSelected={isSelected}
        fill={color}
        {...ghostingStyle}
        {...dataAttributes}
        getPointInViewerCoordinateSystem={getPointInViewerCoordinateSystem}
      />
    );
  } else if (shape.sheetShapeLine) {
    return (
      <SheetPolyLineShape
        sheetShapeLine={shape.sheetShapeLine}
        onClick={onClick}
        isSelected={isSelected}
        stroke={color}
        {...ghostingStyle}
        {...dataAttributes}
        getPointInViewerCoordinateSystem={getPointInViewerCoordinateSystem}
      />
    );
  } else if (shape.sheetShapePoint) {
    return (
      <SheetPointShape
        sheetShapePoint={shape.sheetShapePoint}
        onClick={onClick}
        isSelected={isSelected}
        fill={color}
        {...ghostingStyle}
        {...dataAttributes}
        getPointInViewerCoordinateSystem={getPointInViewerCoordinateSystem}
      />
    );
  }
  return null;
};

const SheetPolygonShape = ({
  sheetShapePolygon,
  isSelected,
  getPointInViewerCoordinateSystem,
  fill,
  ...props
}: {
  sheetShapePolygon: NonNullable<SheetShapeDeepFragment['sheetShapePolygon']>;
  isSelected?: boolean;
  getPointInViewerCoordinateSystem: (pointInPdf: Point2) => Point2;
} & BoxProps) => {
  const { multipolygon } = sheetShapePolygon;

  const pathData = useMemo(() => {
    return transformMultipolygonCoordinates(
      toMultiPolygon2(multipolygon),
      getPointInViewerCoordinateSystem
    ).polygons.map((polygon) => {
      return getPolygon2PathData(polygon);
    });
  }, [getPointInViewerCoordinateSystem, multipolygon]);

  return (
    <chakra.g
      fillRule={'evenodd'}
      fill={fill}
      stroke={isSelected ? 'white' : fill}
      strokeOpacity={1}
      strokeWidth="2"
      fillOpacity={0.6}
      _hover={{
        filter: 'brightness(150%)',
      }}
    >
      {pathData.map((data, index) => (
        <chakra.path key={index} d={data} {...props} />
      ))}
    </chakra.g>
  );
};

const SheetPolyLineShape = ({
  sheetShapeLine,
  isSelected,
  getPointInViewerCoordinateSystem,
  ...props
}: {
  sheetShapeLine: NonNullable<SheetShapeDeepFragment['sheetShapeLine']>;
  isSelected?: boolean;
  getPointInViewerCoordinateSystem: (pointInPdf: Point2) => Point2;
} & BoxProps) => {
  const { points } = sheetShapeLine;

  const domPoints = useMemo(() => {
    return points.map((point) =>
      getPointInViewerCoordinateSystem([point.x, point.y])
    );
  }, [getPointInViewerCoordinateSystem, points]);

  return (
    <chakra.polyline
      points={domPoints.map(([x, y]) => `${x},${y}`).join(' ')}
      strokeWidth="5"
      margin={3}
      fill="none"
      _hover={{
        filter: 'brightness(150%)',
      }}
      {...props}
    />
  );
};

const SheetPointShape = ({
  sheetShapePoint,
  isSelected,
  getPointInViewerCoordinateSystem,
  ...props
}: {
  sheetShapePoint: NonNullable<SheetShapeDeepFragment['sheetShapePoint']>;
  isSelected?: boolean;
  getPointInViewerCoordinateSystem: (pointInPdf: Point2) => Point2;
} & BoxProps) => {
  const { point } = sheetShapePoint;

  const domPoint = useMemo(() => {
    return getPointInViewerCoordinateSystem([point.x, point.y]);
  }, [getPointInViewerCoordinateSystem, point]);

  return (
    <chakra.circle
      cx={domPoint[0]}
      cy={domPoint[1]}
      r={8}
      stroke="black"
      strokeWidth="1"
      _hover={{
        filter: 'brightness(150%)',
      }}
      {...props}
    />
  );
};
function getShapeGhostingStyle(
  isHidden: boolean,
  isGhostingActive: boolean
): BoxProps {
  return isHidden && isGhostingActive
    ? {
        opacity: 0.1,
        pointerEvents: 'none',
      }
    : {};
}

export function isShapeHidden(
  hiddenElements: DbIdsPerModel,
  shapeUrn: string,
  shape: SheetShapeDeepFragment,
  hiddenSheetShapes: DbIdsPerModel,
  isolatedElements: DbIdsPerModel,
  isSelected: boolean
) {
  if (isSelected) {
    return false;
  }
  return (
    hiddenElements[shapeUrn]?.includes(shape.dbId) ||
    hiddenSheetShapes[shapeUrn]?.includes(shape.dbId) ||
    (hasDbIds(isolatedElements) &&
      !isolatedElements[shapeUrn]?.includes(shape.dbId))
  );
}
